import {
    propEq,
    findIndex,
    includes,
    keys,
    prop,
    mapObjIndexed,
    last,
    assoc,
    concat,
    has,
    remove,
    find,
    without,
} from 'ramda';
import * as Config from '../config';
import MAPNOTES from '../../../mock-data/mapNotelets.json';
import FULLMAPNOTES from '../../../mock-data/mapNoteFull.json';

let storedMapNotesFull = FULLMAPNOTES;
let storedMapNotelets = MAPNOTES;
let storedNextMapNoteId = 1;

const getMapNoteById = noteId => {
    const mapNote = storedMapNotesFull.find(note => note.id === noteId);
    return mapNote;
};

const getMapNoteletById = noteId => {
    const mapNotelet = storedMapNotelets.find(note => note.id === noteId);
    return mapNotelet;
};

const mockMapNotelets = async () => {
    storedMapNotelets = MAPNOTES;
    storedMapNotesFull = FULLMAPNOTES;
    return MAPNOTES;
};

const mockMapNote = async noteId => getMapNoteById(noteId);

const mockUpdateMapNote = async (noteId, changes) => {
    // MapNote/mapNotelets that need to change
    const mapNoteToChange = getMapNoteById(noteId);
    const mapNoteletToChange = getMapNoteletById(noteId);
    const addHistoryComment = (key, value) => ({
        newValue: value,
        previousValue: null,
        createdAt: new Date().toISOString(),
        fieldName: key,
        type: 'history',
    });
    const changeFieldValues = (value, key) => (includes(key, keys(changes)) ? prop(key, changes) : value);

    // Indices to change
    const mapNoteletIndexToChange = findIndex(propEq(noteId, 'id'))(storedMapNotelets);
    const mapNoteIndexToChange = findIndex(propEq(noteId, 'id'))(storedMapNotesFull);

    // New mapNote/mapNotelets resulted from the change
    const newMapNotelet = mapObjIndexed(changeFieldValues, mapNoteletToChange);
    const newMapNote = mapObjIndexed(changeFieldValues, mapNoteToChange);

    const lastKey = last(keys(changes));
    const historyComment = addHistoryComment(lastKey, changes[lastKey]);
    const newMapNoteWithHistory = assoc('comments', concat(newMapNote.comments, [historyComment]), newMapNote);
    storedMapNotelets[mapNoteletIndexToChange] = newMapNotelet;
    storedMapNotesFull[mapNoteIndexToChange] = newMapNoteWithHistory;

    return newMapNoteWithHistory;
};

const mockAddMapNoteComment = async (email, noteId, comment) => {
    const mapNote = getMapNoteById(noteId);
    if (!mapNote) return null;
    const mapNoteIndexToChange = findIndex(propEq(noteId, 'id'))(storedMapNotesFull);
    const oldComments = mapNote.comments;
    if (has('comment', comment)) {
        const newComment = {
            body: comment.comment,
            createdAt: new Date().toISOString(),
            createdBy: { email },
            id: storedNextMapNoteId++,
            type: 'comment',
            uploads: [],
        };

        const newMapNote = assoc('comments', concat(oldComments, [newComment]), mapNote);
        storedMapNotesFull[mapNoteIndexToChange] = newMapNote;

        return newMapNote;
    }
    const newComment = {
        body: '',
        createdAt: new Date().toISOString(),
        createdBy: { email },
        id: storedNextMapNoteId++,
        type: 'comment',
        uploads: [
            'https://cdn2-www.dogtime.com/assets/uploads/gallery/golden-retriever-dogs-and-puppies/golden-retriever-dogs-puppies-9.jpg',
        ],
    };

    const newMapNote = assoc('comments', concat(oldComments, [newComment]), mapNote);
    storedMapNotesFull[mapNoteIndexToChange] = newMapNote;

    return newMapNote;
};

const createdMapNoteFull = (newNote, email) => ({
    id: storedNextMapNoteId,
    url: `https://locusmapsrequests.zendesk.com/api/v2/tickets/${storedNextMapNoteId}.json`,
    noteStatus: 'open',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    subject: newNote.subject,
    description: newNote.description,
    noteType: newNote.noteType || 'noType',
    location: {
        venueId: newNote.location.venueId,
        ordinalId: newNote.location.ordinalId,
        latitude: newNote.location.latitude,
        longitude: newNote.location.longitude,
        geohash: newNote.location.geohash,
        structureId: newNote.location.structureId,
        levelId: newNote.location.levelId,
    },
    accountIds: [Config.getAccountId()],
    createdBy: { email },
    comments: [],
    dueAt: new Date().toISOString(),
    isPublic: true,
});

const createdMapNotelet = newNote => ({
    id: storedNextMapNoteId,
    createdAt: new Date().toISOString(),
    subject: newNote.subject,
    description: newNote.description,
    location: {
        venueId: newNote.location.venueId,
        ordinalId: newNote.location.ordinalId,
        latitude: newNote.location.latitude,
        longitude: newNote.location.longitude,
    },
    noteStatus: 'open',
    noteType: newNote.noteType || 'noType',
    accountIds: [Config.getAccountId()],
});

const mockCreateMapNote = async (newNote, email) => {
    const newCreatedMapNoteFull = createdMapNoteFull(newNote, email);
    const newCreatedMapNotelet = createdMapNotelet(newNote);

    storedMapNotelets = concat([newCreatedMapNotelet], storedMapNotelets);
    storedMapNotesFull = concat([newCreatedMapNoteFull], storedMapNotesFull);
    storedNextMapNoteId = ++storedNextMapNoteId;

    return newCreatedMapNoteFull;
};

const mockDeleteMapNote = async noteId => {
    storedMapNotelets = remove(findIndex(propEq(noteId, 'id'))(storedMapNotelets), 1, storedMapNotelets);
    storedMapNotesFull = remove(findIndex(propEq(noteId, 'id'))(storedMapNotesFull), 1, storedMapNotesFull);
};

const mockDeleteMapNoteComment = async (noteId, commentId) => {
    const mapNote = getMapNoteById(noteId);
    const mapNoteCommentToDelete = find(propEq(commentId, 'id'))(mapNote.comments);
    const newCommentsAfterDelete = without([mapNoteCommentToDelete], mapNote.comments);
    const newMapNote = assoc('comments', newCommentsAfterDelete, mapNote);
    storedMapNotesFull[findIndex(propEq(noteId, 'id'))(storedMapNotesFull)] = newMapNote;
    return { ok: true };
};

const MockMapNotes = {
    getMapNotelets: () => mockMapNotelets(),
    getMapNoteTotals: () => Promise.resolve({ open: 4, resolved: 2 }),
    getSelectedMapNote: (venueId, noteId) => mockMapNote(noteId),
    deleteMapNote: (venueId, noteId) => mockDeleteMapNote(noteId),
    updateMapNote: (venueId, noteId, changes) => mockUpdateMapNote(noteId, changes),
    addMapNoteComment: (venueId, email, noteId, comment) => mockAddMapNoteComment(email, noteId, comment),
    createMapNote: (venueId, email, newNote) => mockCreateMapNote(newNote, email),
    deleteMapNoteComment: (venueId, email, noteId, commentId) => mockDeleteMapNoteComment(noteId, commentId),

    // Testing functions
    getMapNoteById,
    getMapNoteletById,
};

export { MockMapNotes };
